import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/cre/Header";
import Footer from "@/components/cre/Footer";
import StatCard from "@/components/cre/StatCard";
import PlanWidget from "@/components/cre/PlanWidget";
import BillingWarning from "@/components/cre/BillingWarning";
import ComingSoon from "@/components/cre/ComingSoon";
import BaselineEditor from "@/components/cre/BaselineEditor";
import TimeseriesChart from "@/components/cre/TimeseriesChart";
import { IncidentRow } from "@/components/cre/dashboard/IncidentRow";
import { SampleLibrary } from "@/components/cre/dashboard/SampleLibrary";
import { Button } from "@/components/ui/button";
import { Activity, LogIn, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    total_incidents: 0,
    active_incidents: 0,
    approved_runbooks: 0,
    rejected_runbooks: 0,
    avg_agents_per_incident: 0,
    total_minutes_saved: 0,
  });
  const [incidents, setIncidents] = useState([]);
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const [plan, setPlan] = useState(null);

  const refresh = async () => {
    try {
      const [s, incs, smp] = await Promise.all([
        api.stats(),
        showArchived ? api.listIncidentsAll() : api.listIncidents(),
        api.listSamples(),
      ]);
      setStats(s);
      setIncidents(incs);
      setSamples(smp);
    } catch (e) {
      toast.error("Failed to load dashboard");
    }
  };

  const deleteIncident = async (id) => {
    try {
      await api.deleteIncident(id);
      toast.success("Analysis deleted");
      refresh();
    } catch (_) {
      toast.error("Delete failed");
    }
  };

  useEffect(() => {
    if (user === undefined) return;
    (async () => {
      setLoading(true);
      await refresh();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived, user]);

  const seedSample = async (key) => {
    setSeeding(key);
    try {
      const inc = await api.createFromSample(key);
      toast.success("Incident created from sample");
      window.location.href = `/incidents/${inc.id}`;
    } catch (e) {
      toast.error("Could not create incident");
    } finally {
      setSeeding(null);
    }
  };

  return (
    <div className="min-h-screen cre-dot-bg flex flex-col">
      <Header />
      <div className="flex-1 max-w-7xl mx-auto w-full px-5 md:px-8 py-8 md:py-10">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="cre-overline text-sky-700">
              {user ? "My Reasoning Workspace" : "Reasoning Workspace"}
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight mt-1 text-slate-900">
              {user ? "My incidents" : "Recent analyses"}
            </h1>
          </div>
          <Link to="/incidents/new" data-testid="dashboard-new-incident">
            <Button className="cre-cta-primary rounded-lg font-semibold h-11">
              <Plus className="h-4 w-4 mr-2" strokeWidth={2} /> Raise New Incident
            </Button>
          </Link>
        </div>

        {user === null ? (
          <div
            data-testid="dashboard-signin-banner"
            className="mt-5 border border-blue-500/30 bg-blue-500/[0.06] rounded-sm p-4 flex items-start gap-3 flex-wrap"
          >
            <LogIn className="h-4 w-4 text-blue-300 mt-0.5" strokeWidth={1.75} />
            <div className="flex-1 min-w-0 text-sm text-zinc-200 leading-relaxed">
              Sign in to analyse incidents. Free accounts include 3 CRE
              analysis runs every month with full multi-agent reasoning.
            </div>
            <Link to="/login" data-testid="dashboard-signin-link">
              <Button
                size="sm"
                className="rounded-sm bg-blue-500 hover:bg-blue-400 text-white font-semibold"
              >
                Sign In
              </Button>
            </Link>
          </div>
        ) : null}

        {user ? (
          <div className="mt-5 space-y-3">
            <BillingWarning plan={plan} />
            <PlanWidget onPlanChange={setPlan} />
          </div>
        ) : null}

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard
            label="Total incidents"
            value={stats.total_incidents}
            hint="across this workspace"
            tid="stat-total"
          />
          <StatCard
            label="Active"
            value={stats.active_incidents}
            hint="pending / analyzing / awaiting"
            tid="stat-active"
          />
          <StatCard
            label="Approved"
            value={stats.approved_runbooks}
            hint="reviewed and authorized"
            tid="stat-approved"
          />
          <StatCard
            label="Avg agents / incident"
            value={stats.avg_agents_per_incident}
            hint="multi-domain coverage"
            tid="stat-avg"
          />
          <StatCard
            label="Minutes saved"
            value={Math.round(stats.total_minutes_saved || 0)}
            hint="vs manual baseline (approved)"
            tid="stat-minutes-saved"
          />
        </div>

        {/* Coming-soon integrations strip */}
        <div className="mt-6 cre-surface rounded-sm p-4 flex items-center flex-wrap gap-3">
          <span className="cre-overline">Integrations</span>
          <span className="text-sm text-zinc-400">
            ServiceNow · Jira · PagerDuty · Slack · Datadog
          </span>
          <span className="ml-auto flex items-center gap-2">
            <ComingSoon variant="ghost" />
            <span className="text-[11px] font-mono text-zinc-500">
              available in future enterprise configurations
            </span>
          </span>
        </div>

        {/* Timeseries + baselines */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <TimeseriesChart refreshKey={refreshTick} />
          </div>
          <div>
            <BaselineEditor
              onSaved={() => {
                setRefreshTick((t) => t + 1);
                refresh();
              }}
            />
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Incidents list */}
          <div className="lg:col-span-2 cre-surface rounded-sm">
            <div className="p-5 border-b border-white/10 flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-blue-300" strokeWidth={1.5} />
                <h2 className="font-heading text-lg font-bold tracking-tight">
                  Recent incidents
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowArchived((v) => !v)}
                  data-testid="toggle-archived"
                  className={`text-[11px] font-mono uppercase tracking-widest rounded-sm px-2 py-1 border transition-colors ${
                    showArchived
                      ? "border-blue-500/40 text-blue-300 bg-blue-500/5"
                      : "border-white/15 text-zinc-400 hover:border-white/25"
                  }`}
                >
                  {showArchived ? "showing archived" : "active only"}
                </button>
                <span className="text-[11px] font-mono text-zinc-500">
                  {incidents.length} shown
                </span>
              </div>
            </div>
            <div className="divide-y divide-white/5">
              {loading ? (
                <div className="p-6 text-sm text-zinc-500">Loading…</div>
              ) : incidents.length === 0 ? (
                <div className="p-6 text-sm text-zinc-500">
                  No incidents yet. Pick a sample on the right to get started.
                </div>
              ) : (
                incidents.map((inc) => (
                  <IncidentRow
                    key={inc.id}
                    inc={inc}
                    user={user}
                    onRefresh={refresh}
                    onDelete={deleteIncident}
                  />
                ))
              )}
            </div>
          </div>

          <SampleLibrary samples={samples} seeding={seeding} onSeed={seedSample} />
        </div>
      </div>
      <Footer />
    </div>
  );
}
